import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { handleApiError } from '../lib/api';
import { User, Truck, Star, Shield, Settings, LogOut, Camera, AlertCircle, Check, ShieldCheck } from 'lucide-react';
import { Agent } from '@ouagaliv/shared';

export default function Profile() {
  const { user, logout, refreshUser, updateProfile, updateAvailability } = useAuth();
  const agentUser = user as Agent | null;
  const [avatarPreview] = useState<string | null>(agentUser?.avatarUrl || null);
  const [formData, setFormData] = useState({
    fullName: agentUser?.fullName || '',
    vehicleType: agentUser?.vehicleType || '',
    vehiclePlate: agentUser?.vehiclePlate || '',
    vehicleColor: agentUser?.vehicleColor || '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'profile' | 'vehicle' | 'security'>('profile');
  const isActive = agentUser?.status === 'active';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      await updateProfile(formData);
      await refreshUser();
      setSuccess('Profil mis à jour');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvailabilityToggle = async (available: boolean) => {
    try {
      await updateAvailability(available);
      setSuccess(available ? 'Vous êtes maintenant disponible' : 'Vous êtes maintenant indisponible');
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const vehicleTypes = [
    { value: 'motorcycle', label: 'Moto', icon: Truck },
    { value: 'bicycle', label: 'Vélo', icon: Truck },
    { value: 'car', label: 'Voiture', icon: Truck },
    { value: 'tricycle', label: 'Tricycle', icon: Truck },
    { value: 'foot', label: 'À pied', icon: Truck },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container pt-4 pb-24">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-text">Mon profil</h1>
        </div>

        <Link
          to="/onboarding"
          className="card mb-4 flex items-center gap-3"
          style={{ textDecoration: 'none', borderColor: isActive ? '#059669' : '#d97706' }}
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-700'}`}>
            <ShieldCheck size={20} />
          </div>
          <div>
            <p className="font-medium text-text">{isActive ? 'Compte activé' : 'Compte en attente d’activation'}</p>
            <p className="text-xs text-text-secondary">{isActive ? 'Voir mes documents et ma formation' : 'Fournir mes documents et suivre la formation'}</p>
          </div>
        </Link>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <Check size={16} />
            {success}
          </div>
        )}

        <div className="flex gap-2 mb-6 border-b border-border">
          {['profile', 'vehicle', 'security'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`flex-1 py-2 text-center font-medium ${activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-text-secondary'}`}
            >
              {tab === 'profile' ? (<span><User size={18} className="inline" /> Profil</span>) : null}
                          {tab === 'vehicle' ? (<span><Truck size={18} className="inline" /> Véhicule</span>) : null}
                          {tab === 'security' ? (<span><Shield size={18} className="inline" /> Sécurité</span>) : null}
            </button>
          ))}
        </div>

        {activeTab === 'profile' && (
          <form onSubmit={handleSubmit} className="card space-y-4">
            <div className="text-center mb-4">
              <div className="relative w-24 h-24 rounded-full bg-gray-200 mx-auto mb-3 overflow-hidden">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User size={48} className="mx-auto text-gray-400" />
                )}
                <span className="absolute bottom-0 right-0 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-white" title="Modification de la photo bientôt disponible">
                  <Camera size={16} />
                </span>
              </div>
              <p className="text-sm text-text-secondary">Modification de la photo bientôt disponible</p>
            </div>

            <div className="input-group">
              <label htmlFor="fullName" className="input-label">Nom complet</label>
              <input
                id="fullName"
                type="text"
                className="input-field"
                value={formData.fullName}
                onChange={e => setFormData({...formData, fullName: e.target.value})}
                placeholder="Votre nom complet"
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="email" className="input-label">Email</label>
              <input
                id="email"
                type="email"
                className="input-field"
                value={user?.email || ''}
                disabled
              />
              <p className="input-hint">L'email ne peut pas être modifié</p>
            </div>

            <div className="input-group">
              <label htmlFor="phone" className="input-label">Téléphone</label>
              <input
                id="phone"
                type="tel"
                className="input-field"
                value={user?.phone || ''}
                disabled
              />
              <p className="input-hint">Le téléphone ne peut pas être modifié</p>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Star size={20} />
                </div>
                <div>
                  <p className="font-medium text-text">Note</p>
                  <p className="text-sm text-text-secondary">{agentUser?.rating?.toFixed(1) || '5.0'} / 5.0 • {agentUser?.totalRides || 0} courses</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                  <Shield size={20} />
                </div>
                <div>
                  <p className="font-medium text-text">Taux d'acceptation</p>
                  <p className="text-sm text-text-secondary">{agentUser?.acceptanceRate?.toFixed(0) || '100'}%</p>
                </div>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
              {isLoading ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
            </button>
          </form>
        )}

        {activeTab === 'vehicle' && (
          <form onSubmit={handleSubmit} className="card space-y-4">
            <h3 className="font-semibold text-text mb-4 flex items-center gap-2">
              <Truck size={20} className="text-primary" />
              Informations véhicule
            </h3>

            <div className="input-group">
              <label className="input-label">Type de véhicule</label>
              <div className="grid grid-cols-3 gap-2">
                {vehicleTypes.map(v => (
                  <button
                    key={v.value}
                    type="button"
                    onClick={() => setFormData({...formData, vehicleType: v.value})}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      formData.vehicleType === v.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <v.icon size={24} className="mx-auto mb-1" />
                    <p className="text-sm font-medium">{v.label}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="vehiclePlate" className="input-label">Plaque d'immatriculation</label>
              <input
                id="vehiclePlate"
                type="text"
                className="input-field"
                value={formData.vehiclePlate}
                onChange={e => setFormData({...formData, vehiclePlate: e.target.value})}
                placeholder="BF-1234-AB"
                style={{textTransform: 'uppercase'}}
              />
            </div>

            <div className="input-group">
              <label htmlFor="vehicleColor" className="input-label">Couleur du véhicule</label>
              <input
                id="vehicleColor"
                type="text"
                className="input-field"
                value={formData.vehicleColor}
                onChange={e => setFormData({...formData, vehicleColor: e.target.value})}
                placeholder="Noir, Blanc, Rouge..."
              />
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
              {isLoading ? 'Sauvegarde...' : 'Sauvegarder le véhicule'}
            </button>

            <div className="pt-4 border-t border-border">
              <h4 className="font-medium text-text mb-3 flex items-center gap-2">
                <Settings size={18} /> Disponibilité
              </h4>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-text">Recevoir des courses</p>
                  <p className="text-sm text-text-secondary">
                    {isActive ? 'Activez pour être notifié des courses proches' : 'Activez d’abord votre compte'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agentUser?.isAvailable || false}
                    onChange={e => handleAvailabilityToggle(e.target.checked)}
                    className="sr-only peer"
                    disabled={isLoading || !isActive}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50"></div>
                </label>
              </div>
            </div>
          </form>
        )}

        {activeTab === 'security' && (
          <div className="card space-y-6">
            <div>
              <h3 className="font-semibold text-text mb-3 flex items-center gap-2">
                <Shield size={20} className="text-primary" />
                Changer le mot de passe
              </h3>
              <p className="text-sm text-text-secondary">Pour des raisons de sécurité, le changement de mot de passe nécessite une réauthentification.</p>
              <Link to="/login" className="btn btn-outline mt-4">Se reconnecter pour changer</Link>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="font-semibold text-text mb-3 flex items-center gap-2">
                <LogOut size={20} className="text-error" />
                Déconnexion
              </h3>
              <p className="text-sm text-text-secondary mb-4">Vous serez déconnecté de tous vos appareils.</p>
              <button onClick={logout} className="btn btn-danger">
                <LogOut size={18} /> Se déconnecter
              </button>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="font-semibold text-text mb-3 flex items-center gap-2">
                <AlertCircle size={20} className="text-warning" />
                Supprimer le compte
              </h3>
              <p className="text-sm text-text-secondary mb-4">Cette action est irréversible. Toutes vos données seront définitivement supprimées.</p>
              <button className="btn btn-ghost text-error">Supprimer mon compte</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}